import { useReducer, createContext, useContext } from "react";
import { useMount, getCkeyFromWindow } from "../../constants/constants";
import {
  determineCurrentTimeZone,
  ONE_MINUTE_OF_MS,
} from "../../utils/timeUtils";
import type {
  DataDumpAggregateType,
  ExpandedProcessTileConfig,
} from "types/types";
import { useTranslation } from "react-i18next";
import { useIsStorybook } from "utils/customHooks/storyHooks";

// SettingsManager: a React Context (simplified global state) for settings (state which does not change frequently)
// ! since this is a React Context, it is not "render-optimized" => when you change any one state, all components that use useSettings() will re-render
// ! so, *only put state in here which is not frequently changed*

export interface SettingsStoreInterface {
  resetAllSettingsToDefaults(): void;
  languagePreference: string;
  setLanguagePreference(lng: string): void;
  autoRefreshTimeInterval: number;
  setAutoRefreshTimeInterval(num: number): void;
  isUsingRateRollingTimeWindow: boolean;
  setIsUsingRateRollingTimeWindow(bool: boolean): void;
  rateRollingTimeWindow: number;
  setRateRollingTimeWindow(num: number): void;
  estimatedFutureMetricPercent: number;
  setEstimatedFutureMetricPercent(num: number): void;
  isPredictedFutureMetric: boolean;
  setisPredictedFutureMetric(bool: boolean): void;
  isAutoRefreshEnabled: boolean;
  setIsAutoRefreshEnabled(bool: boolean): void;
  isTilesPageFullDay: boolean;
  setIsTilesPageFullDay(bool: boolean): void;
  isBabyButterflyVisible: boolean;
  setIsBabyButterflyVisible(bool: boolean): void;
  isPnlBreakdownChartVisible: boolean;
  setIsPnlBreakdownChartVisible(bool: boolean): void;
  isTrendChartVisible: boolean;
  setIsTrendChartVisible(bool: boolean): void;
  isTrendChartVisibleHubletPage: boolean;
  setIsTrendChartVisibleHubletPage(bool: boolean): void;
  isTourEnabled: boolean;
  setIsTourEnabled(bool: boolean): void;
  isLargeTiles: boolean;
  setIsLargeTiles(bool: boolean): void;
  tileWidthMultiplier: number;
  setTileWidthMultiplier(value: number): void;
  tileScaleMultiplier: number;
  setTileScaleMultiplier(value: number): void;
  isRolledUpTiles: boolean;
  setIsRolledUpTiles(bool: boolean): void;
  areMetricsVisible: boolean;
  setAreMetricsVisible(bool: boolean): void;
  shouldShowRateNotCount: boolean;
  setShouldShowRateNotCount(bool: boolean): void;
  shouldFilterBySelectedProcesses: boolean;
  setShouldFilterBySelectedProcesses(bool: boolean): void;
  preferredMetric: keyof DataDumpAggregateType;
  setPreferredMetric(metric: keyof DataDumpAggregateType): void;
  dayStartMsOffset: number;
  setDayStartMsOffset(dayStartMsOffset: number): void;
  isDefaultFullDaySelected: boolean;
  setIsDefaultFullDaySelected(bool: boolean): void;
  shouldUseCustomTimeZone: boolean;
  setShouldUseCustomTimeZone(shouldUseCustomTimeZone: boolean): void;
  timeZoneLocaleString: string;
  setTimeZoneLocaleString(localeString: string): void;
  shouldAutoUpdate: boolean;
  setShouldAutoUpdate(bool: boolean): void;
  showComponentNames: boolean;
  setShowComponentNames(bool: boolean): void;
  areProcessTilesExpanded: boolean;
  setAreProcessTilesExpanded(bool: boolean): void;
  expandedProcessTileConfig: ExpandedProcessTileConfig;
  setExpandedProcessTileConfig(config: ExpandedProcessTileConfig): void;
  shouldStayOnCurrentShift: boolean;
  setShouldStayOnCurrentShift(bool: boolean): void;
  shouldAutoSwitchToCustomerTimezone: boolean;
  setShouldAutoSwitchToCustomerTimezone(bool: boolean): void;
  isHubletPageCompactLayoutDisabled: boolean;
  setIsHubletPageCompactLayoutDisabled(bool: boolean): void;

  settingsToApply: {
    current: boolean;
    localStorageName: string;
    updateFn(value: any): void;
  }[];
}

const ckeyFromWindow = getCkeyFromWindow();
const isNobel = !!(ckeyFromWindow && ckeyFromWindow === "nb");

export const defaultSettingsStore = {
  isTilesPageFullDay: false,
  isBabyButterflyVisible: true,
  isPnlBreakdownChartVisible: true,
  isTrendChartVisible: true,
  isTrendChartVisibleHubletPage: true,
  isTourEnabled: false,
  isLargeTiles: false,
  tileWidthMultiplier: Number(
    window.localStorage.getItem("tileWidthMultiplier") || 1
  ),
  tileScaleMultiplier: Number(
    window.localStorage.getItem("tileScaleMultiplier") || 1
  ),
  isRolledUpTiles: true,
  areMetricsVisible: true,
  preferredMetric: isNobel
    ? "teepPercent"
    : ("oeePercent" as keyof DataDumpAggregateType),
  dayStartMsOffset: 0,
  shouldShowRateNotCount: false,
  isAutoRefreshEnabled: false,
  languagePreference: "en",
  isPredictedFutureMetric: false,
  estimatedFutureMetricPercent: 1,
  rateRollingTimeWindow: 10 * ONE_MINUTE_OF_MS,
  shouldFilterBySelectedProcesses: true,
  isUsingRateRollingTimeWindow: false,
  autoRefreshTimeInterval: 5 * ONE_MINUTE_OF_MS,
  isDefaultFullDaySelected: false,
  timeZoneLocaleString: determineCurrentTimeZone().localeString,
  shouldUseCustomTimeZone: false,
  shouldAutoUpdate: false,
  showComponentNames: false,
  areProcessTilesExpanded: false,
  expandedProcessTileConfig: {
    // this is the order that the toggles appear in ProcessTilesConfig
    isTileMetricPercentVisible: true,
    isOperatorNameVisible: false,
    isSkuIdVisible: false,
    isSkuNameVisible: false,
    isTileCountVisible: false,
    isTileAheadBehindVisible: false,
    isTileButterflyVisible: true,
    isTeamSizeVisible: false,
    isTopLossesVisible: false,
  },
  shouldStayOnCurrentShift: true,
  shouldAutoSwitchToCustomerTimezone: true,
  isHubletPageCompactLayoutDisabled: false,
};

export const defaultSettingsContext: SettingsStoreInterface = {
  ...defaultSettingsStore,

  resetAllSettingsToDefaults: () => {
    return;
  },
  setShouldUseCustomTimeZone: () => {
    return;
  },
  setTimeZoneLocaleString: () => {
    return;
  },
  setIsDefaultFullDaySelected: () => {
    return;
  },
  setShouldAutoUpdate: () => {
    return;
  },
  setShowComponentNames: () => {
    return;
  },
  setAreProcessTilesExpanded: () => {
    return;
  },
  setExpandedProcessTileConfig: () => {
    return;
  },
  setAutoRefreshTimeInterval: () => {
    return;
  },
  setLanguagePreference: () => {
    return;
  },
  setIsUsingRateRollingTimeWindow: () => {
    return;
  },
  setShouldFilterBySelectedProcesses: () => {
    return;
  },
  setisPredictedFutureMetric: () => {
    return;
  },
  setRateRollingTimeWindow: () => {
    return;
  },
  setEstimatedFutureMetricPercent: () => {
    return;
  },
  setIsAutoRefreshEnabled: () => {
    return;
  },
  setIsTilesPageFullDay: () => {
    return;
  },
  setIsBabyButterflyVisible: () => {
    return;
  },
  setIsPnlBreakdownChartVisible: () => {
    return;
  },
  setIsTrendChartVisible: () => {
    return;
  },
  setIsTrendChartVisibleHubletPage: () => {
    return;
  },
  setIsTourEnabled: () => {
    return;
  },
  setIsLargeTiles: () => {
    return;
  },
  setTileWidthMultiplier: () => {
    return;
  },
  setTileScaleMultiplier: () => {
    return;
  },
  setIsRolledUpTiles: () => {
    return;
  },
  setAreMetricsVisible: () => {
    return;
  },
  setPreferredMetric: () => {
    return;
  },
  setDayStartMsOffset: () => {
    return;
  },
  setShouldShowRateNotCount: () => {
    return;
  },
  setShouldStayOnCurrentShift: () => {
    return;
  },
  setShouldAutoSwitchToCustomerTimezone: () => {
    return;
  },
  setIsHubletPageCompactLayoutDisabled: () => {
    return;
  },
  settingsToApply: [],
};

const reducer = (state, newState) => {
  return { ...state, ...newState };
};

export const SettingsContext = createContext(defaultSettingsContext);

// tslint:disable-next-line: no-big-function
export const SettingsManager = ({
  children,
  settingsOverrides = defaultSettingsStore,
}: {
  children: any;
  settingsOverrides?: Partial<SettingsStoreInterface>;
}) => {
  const initialSettingsStoreState = {
    ...defaultSettingsStore,
    ...settingsOverrides,
  };
  const [state, dispatch] = useReducer(reducer, initialSettingsStoreState);
  const resetAllSettingsToDefaults = () => {
    dispatch(initialSettingsStoreState);
    // clear all localStorage settings except auth token info
    Object.keys(window.localStorage)
      .filter(lsKey => !["id_token", "token_expiry"].includes(lsKey))
      .forEach(lsKey => {
        window.localStorage.removeItem(lsKey);
      });
  };
  const { i18n } = useTranslation("translation");

  // settings with window.localStorage are saved to / loaded from LS
  // other settings reset on each reload
  const setAutoRefreshTimeInterval = (num: number) => {
    window.localStorage.setItem("autoRefreshTimeInterval", num.toString());
    dispatch({ autoRefreshTimeInterval: num });
  };

  const setLanguagePreference = lng => {
    window.localStorage.setItem("languagePreference", lng);

    window.localStorage.setItem("i18nextLng", lng);
    dispatch({ languagePreference: lng });

    // update the i18n.language
    if (i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }
  };
  // * first-time users only
  // set the languagePreference to your i18n.language
  const isStorybook = useIsStorybook();
  useMount(() => {
    const isFirstTimeUser = !window.localStorage.getItem("isFirstTimeAppSetup");
    if (isStorybook) {
      setLanguagePreference(
        settingsOverrides.languagePreference ||
          defaultSettingsStore.languagePreference
      );
    } else if (isFirstTimeUser) {
      // if we start out with en-GB, en-US, or en-CA etc, just use "en" for now
      const isEnglish = i18n.language.slice(0, 2) === "en";
      setLanguagePreference(isEnglish ? "en" : i18n.language);
    }
  });

  const setShowComponentNames = (bool: boolean) => {
    window.localStorage.setItem("showComponentNames", bool.toString());
    dispatch({ showComponentNames: bool.toString() === "true" });
  };
  const setAreProcessTilesExpanded = (bool: boolean) => {
    window.localStorage.setItem("areProcessTilesExpanded", bool.toString());
    dispatch({ areProcessTilesExpanded: bool.toString() === "true" });
  };
  const setExpandedProcessTileConfig = configObj => {
    const isString = typeof configObj === "string";
    window.localStorage.setItem(
      "expandedProcessTileConfig",
      isString ? configObj : JSON.stringify(configObj)
    );
    dispatch({
      expandedProcessTileConfig: isString ? JSON.parse(configObj) : configObj,
    });
  };
  const setShouldAutoUpdate = (bool: boolean) => {
    window.localStorage.setItem("shouldAutoUpdate", bool.toString());
    dispatch({ shouldAutoUpdate: bool.toString() === "true" });
  };
  const setIsDefaultFullDaySelected = (bool: boolean) => {
    window.localStorage.setItem("isDefaultFullDaySelected", bool.toString());
    dispatch({ isDefaultFullDaySelected: bool.toString() === "true" });
  };
  const setIsUsingRateRollingTimeWindow = (bool: boolean) => {
    window.localStorage.setItem(
      "isUsingRateRollingTimeWindow",
      bool.toString()
    );
    dispatch({ isUsingRateRollingTimeWindow: bool.toString() === "true" });
  };
  const setShouldFilterBySelectedProcesses = (bool: boolean) => {
    window.localStorage.setItem(
      "shouldFilterBySelectedProcesses",
      bool.toString()
    );
    dispatch({ shouldFilterBySelectedProcesses: bool.toString() === "true" });
  };
  const setEstimatedFutureMetricPercent = (num: number) => {
    window.localStorage.setItem("estimatedFutureMetricPercent", num.toString());
    dispatch({ estimatedFutureMetricPercent: Number(num) });
  };
  const setRateRollingTimeWindow = (num: number) => {
    window.localStorage.setItem("rateRollingTimeWindow", num.toString());
    dispatch({ rateRollingTimeWindow: Number(num) });
  };
  const setisPredictedFutureMetric = (bool: boolean) => {
    window.localStorage.setItem("isPredictedFutureMetric", bool.toString());
    dispatch({ isPredictedFutureMetric: bool.toString() === "true" });
  };
  const setIsAutoRefreshEnabled = (bool: boolean) => {
    window.localStorage.setItem("isAutoRefreshEnabled", bool.toString());
    dispatch({ isAutoRefreshEnabled: bool.toString() === "true" });
  };
  const setIsTilesPageFullDay = (bool: boolean) => {
    window.localStorage.setItem("isTilesPageFullDay", bool.toString());
    dispatch({ isTilesPageFullDay: bool.toString() === "true" });
  };
  const setIsPnlBreakdownChartVisible = (bool: boolean) => {
    window.localStorage.setItem("isPnlBreakdownChartVisible", bool.toString());
    dispatch({ isPnlBreakdownChartVisible: bool.toString() === "true" });
  };
  const setIsTrendChartVisible = (bool: boolean) => {
    window.localStorage.setItem("isTrendChartVisible", bool.toString());
    dispatch({ isTrendChartVisible: bool.toString() === "true" });
  };
  const setIsTrendChartVisibleHubletPage = (bool: boolean) => {
    window.localStorage.setItem(
      "isTrendChartVisibleHubletPage",
      bool.toString()
    );
    dispatch({ isTrendChartVisibleHubletPage: bool.toString() === "true" });
  };
  const setIsBabyButterflyVisible = (bool: boolean) => {
    window.localStorage.setItem("isBabyButterflyVisible", bool.toString());
    dispatch({ isBabyButterflyVisible: bool.toString() === "true" });
  };
  const setIsTourEnabled = (bool: boolean) => {
    window.localStorage.setItem("isTourEnabled", bool.toString());
    dispatch({ isTourEnabled: bool.toString() === "true" });
  };
  const setIsLargeTiles = (bool: boolean) => {
    window.localStorage.setItem("isLargeTiles", bool.toString());
    dispatch({ isLargeTiles: bool.toString() === "true" });
  };
  const setIsRolledUpTiles = (bool: boolean) => {
    window.localStorage.setItem("isRolledUpTiles", bool.toString());
    dispatch({ isRolledUpTiles: bool.toString() === "true" });
  };
  const setShouldUseCustomTimeZone = (bool: boolean) => {
    window.localStorage.setItem("shouldUseCustomTimeZone", bool.toString());
    dispatch({ shouldUseCustomTimeZone: bool.toString() === "true" });
  };
  const setTimeZoneLocaleString = (timeZoneLocaleString: string) => {
    window.localStorage.setItem(
      "timeZoneLocaleString",
      timeZoneLocaleString.toString()
    );
    dispatch({ timeZoneLocaleString });
  };
  const setAreMetricsVisible = (bool: boolean) => {
    window.localStorage.setItem("areMetricsVisible", bool.toString());
    dispatch({ areMetricsVisible: bool.toString() === "true" });
  };
  const setPreferredMetric = preferredMetric => {
    window.localStorage.setItem("preferredMetric", preferredMetric);
    dispatch({ preferredMetric });
  };
  const setDayStartMsOffset = (dayStartMsOffset: number | string) => {
    window.localStorage.setItem(
      "dayStartMsOffset",
      dayStartMsOffset.toString()
    );
    dispatch({ dayStartMsOffset: Number(dayStartMsOffset) });
  };
  const setShouldShowRateNotCount = (bool: boolean) => {
    window.localStorage.setItem("shouldShowRateNotCount", bool.toString());
    dispatch({ shouldShowRateNotCount: bool.toString() === "true" });
  };
  const setShouldStayOnCurrentShift = (bool: boolean) => {
    window.localStorage.setItem("shouldStayOnCurrentShift", bool.toString());
    dispatch({ shouldStayOnCurrentShift: bool.toString() === "true" });
  };
  const setShouldAutoSwitchToCustomerTimezone = (bool: boolean) => {
    window.localStorage.setItem(
      "shouldAutoSwitchToCustomerTimezone",
      bool.toString()
    );
    dispatch({
      shouldAutoSwitchToCustomerTimezone: bool.toString() === "true",
    });
  };
  const setIsHubletPageCompactLayoutDisabled = (bool: boolean) => {
    window.localStorage.setItem(
      "isHubletPageCompactLayoutDisabled",
      bool.toString()
    );
    dispatch({
      isHubletPageCompactLayoutDisabled: bool.toString() === "true",
    });
  };

  const setTileWidthMultiplier = (value: number) => {
    window.localStorage.setItem("tileWidthMultiplier", value.toString());
    dispatch({ tileWidthMultiplier: Number(value) });
  };

  const setTileScaleMultiplier = (value: number) => {
    window.localStorage.setItem("tileScaleMultiplier", value.toString());
    dispatch({ tileScaleMultiplier: Number(value) });
  };

  // these settings save / load from local storage
  const settingsToApply = [
    {
      current: state.languagePreference,
      localStorageName: "languagePreference",
      updateFn: setLanguagePreference,
    },
    {
      current: state.areProcessTilesExpanded,
      localStorageName: "areProcessTilesExpanded",
      updateFn: setAreProcessTilesExpanded,
    },
    {
      current: state.expandedProcessTileConfig,
      localStorageName: "expandedProcessTileConfig",
      updateFn: setExpandedProcessTileConfig,
    },
    {
      current: state.showComponentNames,
      localStorageName: "showComponentNames",
      updateFn: setShowComponentNames,
    },
    {
      current: state.shouldAutoUpdate,
      localStorageName: "shouldAutoUpdate",
      updateFn: setShouldAutoUpdate,
    },
    {
      current: state.isDefaultFullDaySelected,
      localStorageName: "isDefaultFullDaySelected",
      updateFn: setIsDefaultFullDaySelected,
    },
    {
      current: state.autoRefreshTimeInterval,
      localStorageName: "autoRefreshTimeInterval",
      updateFn: setAutoRefreshTimeInterval,
    },
    {
      current: state.isUsingRateRollingTimeWindow,
      localStorageName: "isUsingRateRollingTimeWindow",
      updateFn: setIsUsingRateRollingTimeWindow,
    },
    {
      current: state.shouldFilterBySelectedProcesses,
      localStorageName: "shouldFilterBySelectedProcesses",
      updateFn: setShouldFilterBySelectedProcesses,
    },
    {
      current: state.rateRollingTimeWindow,
      localStorageName: "rateRollingTimeWindow",
      updateFn: setRateRollingTimeWindow,
    },
    {
      current: state.isBabyButterflyVisible,
      localStorageName: "isBabyButterflyVisible",
      updateFn: setIsBabyButterflyVisible,
    },
    {
      current: state.isTourEnabled,
      localStorageName: "isTourEnabled",
      updateFn: setIsTourEnabled,
    },
    {
      current: state.isLargeTiles,
      localStorageName: "isLargeTiles",
      updateFn: setIsLargeTiles,
    },
    {
      current: state.tileWidthMultiplier,
      localStorageName: "tileWidthMultiplier",
      updateFn: setTileWidthMultiplier,
    },
    {
      current: state.tileScaleMultiplier,
      localStorageName: "tileScaleMultiplier",
      updateFn: setTileScaleMultiplier,
    },
    {
      current: state.isRolledUpTiles,
      localStorageName: "isRolledUpTiles",
      updateFn: setIsRolledUpTiles,
    },
    {
      current: state.areMetricsVisible,
      localStorageName: "areMetricsVisible",
      updateFn: setAreMetricsVisible,
    },
    {
      current: state.preferredMetric,
      localStorageName: "preferredMetric",
      updateFn: setPreferredMetric,
    },
    {
      current: state.shouldShowRateNotCount,
      localStorageName: "shouldShowRateNotCount",
      updateFn: setShouldShowRateNotCount,
    },
    {
      current: state.timeZoneLocaleString,
      localStorageName: "timeZoneLocaleString",
      updateFn: setTimeZoneLocaleString,
    },
    {
      current: state.shouldUseCustomTimeZone,
      localStorageName: "shouldUseCustomTimeZone",
      updateFn: setShouldUseCustomTimeZone,
    },
    {
      current: state.shouldStayOnCurrentShift,
      localStorageName: "shouldStayOnCurrentShift",
      updateFn: setShouldStayOnCurrentShift,
    },
    {
      current: state.shouldAutoSwitchToCustomerTimezone,
      localStorageName: "shouldAutoSwitchToCustomerTimezone",
      updateFn: setShouldAutoSwitchToCustomerTimezone,
    },
    {
      current: state.isHubletPageCompactLayoutDisabled,
      localStorageName: "isHubletPageCompactLayoutDisabled",
      updateFn: setIsHubletPageCompactLayoutDisabled,
    },
    {
      current: state.dayStartMsOffset,
      localStorageName: "dayStartMsOffset",
      updateFn: setDayStartMsOffset,
    },
  ];

  // to prevent unnecessary re-renders we save the value to an object https://reactjs.org/docs/context.html#caveats
  // (doesn't work with state as in the example)
  const value = {
    shouldUseCustomTimeZone: state.shouldUseCustomTimeZone,
    setShouldUseCustomTimeZone,
    timeZoneLocaleString: state.timeZoneLocaleString,
    setTimeZoneLocaleString,
    shouldAutoUpdate: state.shouldAutoUpdate,
    setShouldAutoUpdate,
    showComponentNames: state.showComponentNames,
    setShowComponentNames,
    areProcessTilesExpanded: state.areProcessTilesExpanded,
    setAreProcessTilesExpanded,
    expandedProcessTileConfig: state.expandedProcessTileConfig,
    setExpandedProcessTileConfig,
    isDefaultFullDaySelected: state.isDefaultFullDaySelected,
    setIsDefaultFullDaySelected,
    autoRefreshTimeInterval: state.autoRefreshTimeInterval,
    setAutoRefreshTimeInterval,
    languagePreference: state.languagePreference,
    setLanguagePreference,
    isUsingRateRollingTimeWindow: state.isUsingRateRollingTimeWindow,
    setIsUsingRateRollingTimeWindow,
    shouldFilterBySelectedProcesses: state.shouldFilterBySelectedProcesses,
    setShouldFilterBySelectedProcesses,
    rateRollingTimeWindow: state.rateRollingTimeWindow,
    setRateRollingTimeWindow,
    estimatedFutureMetricPercent: state.estimatedFutureMetricPercent,
    setEstimatedFutureMetricPercent,
    isPredictedFutureMetric: state.isPredictedFutureMetric,
    setisPredictedFutureMetric,
    isAutoRefreshEnabled: state.isAutoRefreshEnabled,
    setIsAutoRefreshEnabled,
    isTilesPageFullDay: state.isTilesPageFullDay,
    setIsTilesPageFullDay,
    isPnlBreakdownChartVisible: state.isPnlBreakdownChartVisible,
    setIsPnlBreakdownChartVisible,
    isTrendChartVisible: state.isTrendChartVisible,
    setIsTrendChartVisible,
    isTrendChartVisibleHubletPage: state.isTrendChartVisibleHubletPage,
    setIsTrendChartVisibleHubletPage,
    isBabyButterflyVisible: state.isBabyButterflyVisible,
    setIsBabyButterflyVisible,
    isTourEnabled: state.isTourEnabled,
    setIsTourEnabled,
    isLargeTiles: state.isLargeTiles,
    setIsLargeTiles,
    tileWidthMultiplier: state.tileWidthMultiplier,
    setTileWidthMultiplier,
    tileScaleMultiplier: state.tileScaleMultiplier,
    setTileScaleMultiplier,
    isRolledUpTiles: state.isRolledUpTiles,
    setIsRolledUpTiles,
    areMetricsVisible: state.areMetricsVisible,
    setAreMetricsVisible,
    preferredMetric: state.preferredMetric,
    setPreferredMetric,
    dayStartMsOffset: state.dayStartMsOffset,
    setDayStartMsOffset,
    shouldShowRateNotCount: state.shouldShowRateNotCount,
    setShouldShowRateNotCount,
    shouldStayOnCurrentShift: state.shouldStayOnCurrentShift,
    setShouldStayOnCurrentShift,
    shouldAutoSwitchToCustomerTimezone:
      state.shouldAutoSwitchToCustomerTimezone,
    setShouldAutoSwitchToCustomerTimezone,
    isHubletPageCompactLayoutDisabled: state.isHubletPageCompactLayoutDisabled,
    setIsHubletPageCompactLayoutDisabled,
    settingsToApply,
    resetAllSettingsToDefaults,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
